import React from "react";
import { SkillCheckInformation } from "../api";

type SkillCheckInfoProps = {
  skillCheckInfo: SkillCheckInformation;
};

type BreakdownEntry = {
  id?: string;
  name?: string;
  type?: string;
  value?: number | string | boolean | null;
};

const DICE_FACES = ["", "\u2680", "\u2681", "\u2682", "\u2683", "\u2684", "\u2685"];

const formatSigned = (value?: number) => {
  if (value === undefined || Number.isNaN(value)) {
    return "0";
  }

  return value > 0 ? `+${value}` : `${value}`;
};

const parseBreakdown = (value: unknown): BreakdownEntry[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((item): item is Record<string, unknown> => !!item && typeof item === "object")
    .map((item) => ({
      id: typeof item.id === "string" ? item.id : undefined,
      name: typeof item.name === "string" ? item.name : undefined,
      type: typeof item.type === "string" ? item.type : undefined,
      value:
        typeof item.value === "number" ||
        typeof item.value === "string" ||
        typeof item.value === "boolean" ||
        item.value === null
          ? item.value
          : undefined,
    }));
};

const toDiceFace = (value: number) => {
  if (value >= 1 && value <= 6) {
    return DICE_FACES[value];
  }

  return `${value}`;
};

export const SkillCheckInfo: React.FC<SkillCheckInfoProps> = ({ skillCheckInfo }) => {
  const rolledDice = Array.isArray(skillCheckInfo.rolledDice) ? skillCheckInfo.rolledDice : [];
  const notes = Array.isArray(skillCheckInfo.notes) ? skillCheckInfo.notes : [];
  const breakdown = parseBreakdown(skillCheckInfo.modificationBreakdown);
  const successThreshold = skillCheckInfo.successThreshold ?? 5;
  const requiredSuccesses = skillCheckInfo.requiredSuccesses ?? 1;
  const successes = skillCheckInfo.successes ?? 0;

  return (
    <div className="skill-check-information-banner">
      <div className="skill-check-information-column">
        <div className="skill-check-information-header">
            <span className="skill-check-information-label">Skill Check</span>
            {skillCheckInfo.stage && (
            <span className="skill-check-information-stage">{skillCheckInfo.stage}</span>
            )}
        </div>

        <div className="skill-check-information-summary">
            <strong>{skillCheckInfo.investigatorName || "Unknown investigator"}</strong>
            {" - "}
            <span>{skillCheckInfo.skillName || "Unknown skill"}</span>
            {skillCheckInfo.baseSkillName &&
            skillCheckInfo.skillName &&
            skillCheckInfo.baseSkillName.toLowerCase() !==
                skillCheckInfo.skillName.toLowerCase() && (
                <span className="skill-check-information-base-skill">
                {" "}
                (uses {skillCheckInfo.baseSkillName})
                </span>
            )}
            {skillCheckInfo.monsterName && (
            <span className="skill-check-information-monster">
                {" "}
                vs {skillCheckInfo.monsterName}
            </span>
            )}
        </div>

        <div className="skill-check-information-stats">
            <span>
            Dice: <strong>{skillCheckInfo.diceToRoll ?? 0}</strong>
            </span>
            <span>
            Modifier: <strong>{formatSigned(skillCheckInfo.modifier)}</strong>
            </span>
            <span>
            Threshold: <strong>{successThreshold}+</strong>
            </span>
            <span>
            Successes:{" "}
            <strong>
                {successes}/{requiredSuccesses}
            </strong>
            </span>
            {typeof skillCheckInfo.isSucceeded === "boolean" && (
            <span className={skillCheckInfo.isSucceeded ? "skill-check-success" : "skill-check-failure"}>
                {skillCheckInfo.isSucceeded ? "Succeeded" : "Failed"}
            </span>
            )}
        </div>

        {skillCheckInfo.bonusSuccessesFromSpendTwoClues !== undefined &&
            skillCheckInfo.bonusSuccessesFromSpendTwoClues > 0 && (
            <div className="skill-check-information-bonus">
                Bonus successes from spending clues:{" "}
                <strong>+{skillCheckInfo.bonusSuccessesFromSpendTwoClues}</strong>
            </div>
            )}

        {rolledDice.length > 0 && (
            <div className="skill-check-information-dice-row">
            <div className="skill-check-information-dice-list">
                {rolledDice.map((roll, index) => (
                <span
                    key={`${roll}-${index}`}
                    className={`skill-check-information-die ${
                    roll >= successThreshold
                        ? "skill-check-information-die-success"
                        : "skill-check-information-die-failure"
                    }`}
                >
                    <span>{toDiceFace(roll)}</span>
                </span>
                ))}
            </div>
            </div>
        )}
      </div>

        <div className="skill-check-information-column">
            {breakdown.length > 0 && (
                <div className="skill-check-information-breakdown">
                <ul>
                    {breakdown.map((entry, index) => (
                    <li key={`${entry.id || entry.name || entry.type || "entry"}-${index}`}>
                        <strong>{entry.name || entry.id || "Modification"}</strong>
                        {entry.type ? ` (${entry.type})` : ""}
                        {entry.value !== undefined ? `: ${String(entry.value)}` : ""}
                    </li>
                    ))}
                </ul>
                </div>
            )}

            {notes.length > 0 && (
                <div className="skill-check-information-notes">
                <span className="skill-check-information-subtitle">Notes:</span>
                <ul>
                    {notes.map((note, index) => (
                    <li key={`${note}-${index}`}>{note}</li>
                    ))}
                </ul>
                </div>
            )}
        </div>

    </div>
  );
};


